import Bugsnag from '@bugsnag/browser';
import {
  ExternalAuthenticationProvider,
  ExternalAuthenticationProviderParams,
  ExternalAuthResult,
} from '@src/features/payments/external-auth/types';
import { createHiddenInput, waitForElement } from '@src/features/payments/external-auth/utils';
import { markExternalAuthChallengeShown } from '@src/features/payments/store/payment';
import type { AppDispatch } from '@src/core/store';
import {
  EXTERNAL_AUTHENTICATION_RETURN_MESSAGE,
  MALGA_CHALLENGE_CONTAINER_ID,
  MALGA_CHALLENGE_CONTAINER_TIMEOUT_MS,
  MALGA_CHALLENGE_TIMEOUT_MS,
  MALGA_COLLECTION_TIMEOUT_MS,
} from './constants';
import type { MalgaChallengeData, MalgaSetupData, PostTokenInFrameParams } from './types';

/**
 * Malga's 3DS2 has no SDK: the checkout posts the collection token to the provider in a hidden iframe,
 * which is how the issuer gets the device data it decides on, and the transact then carries the setupId
 * that ties the charge to that collection. Everything else the charge needs, the backend reads from the
 * request itself.
 */
export class MalgaProvider implements ExternalAuthenticationProvider {
  async handleAuthentication(params: ExternalAuthenticationProviderParams): Promise<ExternalAuthResult> {
    const data = params.paymentMethodExternalAuthentication.data;

    // A transaction that came back waiting on the buyer: the step comes after the transact
    if (data['stepUrl'] || data['stepToken']) {
      return handleChallenge(data as MalgaChallengeData, params.dispatch);
    }

    return handleCollect(data as MalgaSetupData);
  }
}

/** Collects the device data before the transact, which then carries the setupId that ties the charge to it. */
const handleCollect = async ({ setupId, collectUrl, collectToken }: MalgaSetupData): Promise<ExternalAuthResult> => {
  if (!setupId || !collectUrl || !collectToken) {
    Bugsnag.notify({ name: 'MalgaExternalAuth', message: 'Malga 3DS setup answered without its session' });

    return { proceed: false, error: 'tds_generic_error', externalAuthentication: undefined };
  }

  // A collection that does not finish is retried by the attempts manager with the same session
  if (!(await collectDeviceData(collectUrl, collectToken))) {
    return { proceed: false, externalAuthentication: undefined };
  }

  return { proceed: true, externalAuthentication: { setupId } };
};

/**
 * Shows the issuer's challenge: the step token is posted to stepUrl inside a frame in the challenge modal.
 * The issuer sends that frame back to the buyer API, whose page reads the outcome and posts the return
 * message; proceeding only means the challenge ended, not that it was passed — the transaction says that.
 */
const handleChallenge = async (
  { stepUrl, stepToken, returnOrigin }: MalgaChallengeData,
  dispatch: AppDispatch,
): Promise<ExternalAuthResult> => {
  if (!stepUrl || !stepToken || !returnOrigin) {
    Bugsnag.notify({ name: 'MalgaExternalAuth', message: 'Malga 3DS challenge came without its step' });

    return { proceed: false, error: 'tds_generic_error', externalAuthentication: { reason: 'missingStep' } };
  }

  const container = await waitForElement(MALGA_CHALLENGE_CONTAINER_ID, MALGA_CHALLENGE_CONTAINER_TIMEOUT_MS);
  if (!container) {
    Bugsnag.notify({ name: 'MalgaExternalAuth', message: 'Malga 3DS challenge container was not mounted' });

    return { proceed: false, error: 'tds_generic_error', externalAuthentication: { reason: 'containerNotFound' } };
  }

  const ended = showChallenge(container, stepUrl, stepToken, returnOrigin);
  // The frame is posted by now: the buyer is looking at the issuer's screen
  dispatch(markExternalAuthChallengeShown());

  if (!(await ended)) {
    return { proceed: false, error: 'tds_generic_error', externalAuthentication: { reason: 'timeout' } };
  }

  return { proceed: true, externalAuthentication: undefined };
};

/**
 * Posts the collection token to collectUrl inside a hidden iframe and resolves once the provider
 * reports it is done, or with false on timeout.
 *
 * Only the provider's profile.completed message from collectUrl's own origin counts: a page embedding
 * the checkout may run its own Cardinal session, whose messages come from the same origin. The host
 * differs between production and sandbox, so the origin is read from collectUrl rather than fixed. The
 * first such message settles it: Malga's sample continues on every message, which would charge twice.
 */
export const collectDeviceData = async (collectUrl: string, collectToken: string): Promise<boolean> => {
  const { origin } = new URL(collectUrl);

  const container = document.createElement('div');
  container.style.display = 'none';
  document.body.appendChild(container);

  try {
    return await postTokenInFrame({
      container,
      url: collectUrl,
      token: collectToken,
      timeoutMs: MALGA_COLLECTION_TIMEOUT_MS,
      isDone: (event) => event.origin === origin && isProfileCompleted(event.data),
    });
  } finally {
    container.remove();
  }
};

// Cardinal posts its collection result as a JSON string: {"MessageType":"profile.completed","SessionId":...}
const isProfileCompleted = (data: unknown): boolean => {
  try {
    const message = typeof data === 'string' ? JSON.parse(data) : data;

    return message?.MessageType === 'profile.completed';
  } catch {
    return false;
  }
};

/**
 * Posts the step token to stepUrl inside a frame in the container and resolves once the return page
 * reports the challenge ended, or with false on timeout. Only a message from the return page's origin,
 * which the backend sends along with the challenge, counts.
 */
export const showChallenge = (
  container: HTMLElement,
  stepUrl: string,
  stepToken: string,
  returnOrigin: string,
): Promise<boolean> =>
  postTokenInFrame({
    container,
    url: stepUrl,
    token: stepToken,
    timeoutMs: MALGA_CHALLENGE_TIMEOUT_MS,
    isDone: (event) => event.origin === returnOrigin && event.data?.type === EXTERNAL_AUTHENTICATION_RETURN_MESSAGE,
  });

/**
 * How Malga's 3DS2 talks to the issuer's provider: the token is posted as JWT to a frame in the container,
 * and the provider answers with a window message. Resolves on the first message isDone accepts, or with
 * false on timeout, and takes the frame out either way.
 */
const postTokenInFrame = ({ container, url, token, timeoutMs, isDone }: PostTokenInFrameParams): Promise<boolean> =>
  new Promise((resolve) => {
    const iframeName = `malga3ds${Date.now()}`;

    const iframe = document.createElement('iframe');
    iframe.name = iframeName;

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.target = iframeName;
    form.style.display = 'none';
    createHiddenInput(form, 'JWT', token);

    container.append(iframe, form);

    let settled = false;
    const finish = (done: boolean) => {
      if (settled) {
        return;
      }

      settled = true;
      window.removeEventListener('message', onMessage);
      clearTimeout(timeoutId);
      iframe.remove();
      form.remove();
      resolve(done);
    };

    const onMessage = (event: MessageEvent) => {
      if (isDone(event)) {
        finish(true);
      }
    };

    window.addEventListener('message', onMessage);
    const timeoutId = setTimeout(() => finish(false), timeoutMs);

    form.submit();
  });
